/**
 * Base class for Trace format scene loader plugins.
 *
 * Provides shared functionality for loading scene formats (.trace, .zip, .real).
 * Extracts assets from ZIP archives and creates TransformNodes with transforms.
 * Actual asset loading is delegated to behaviors (Model, Image, Video).
 *
 * Note: GltfSceneLoaderPlugin is standalone and doesn't extend this class.
 * TraceXSceneLoaderPlugin extends GltfSceneLoaderPlugin directly.
 */

import type {
    ISceneLoaderPluginAsync,
    ISceneLoaderAsyncResult,
    ISceneLoaderProgressEvent,
    ISceneLoaderPluginExtensions,
} from "@babylonjs/core/Loading/sceneLoader";
import { Scene } from "@babylonjs/core/scene";
import { AssetContainer } from "@babylonjs/core/assetContainer";
import type { LoadedSceneNode, LoadedSceneResult } from './LoadedSceneResult';
import { buildTransformHierarchy } from './sceneLoadingUtils';
import { Logger } from '../../utils/Logger';

// =============================================================================
// Types
// =============================================================================

/**
 * Parsed scene structure - common format for Trace scene file types
 */
export interface ParsedSceneStructure {
    /** Scene objects with their assets and transforms */
    nodes: ParsedSceneNode[];
    /** Map of asset path to blob (for storing in ResourceManager) */
    assetBlobs: Map<string, Blob>;
    /** Root URL for resolving relative asset paths */
    rootUrl: string;
    /** Script @type to asset path map (from scene JSON "scripts" field) */
    scripts?: Record<string, string>;
}

export interface ParsedSceneNode {
    /** Node name */
    name: string;
    /** Original src path from scene data (e.g., "./assets/model.glb") - used by behaviors */
    src?: string;
    /** Transform data */
    transform?: {
        translation?: [number, number, number];
        rotation?: [number, number, number, number];
        scale?: [number, number, number];
        transition?: { duration: number; easing?: string; delay?: number };
    };
    /** Parent node name */
    parentName?: string;
    /** Whether the node is active */
    active?: boolean;
    /** Behaviors */
    behaviors?: unknown[];
    /** When true, Model behavior resets the GLB root transform to identity.
     *  Used by scene formats where the scene transform is the sole source of
     *  truth and any GLB root transform should be discarded. */
    resetAssetTransform?: boolean;
}

// =============================================================================
// Base Plugin Class
// =============================================================================

/**
 * Abstract base class for Trace format scene loader plugins.
 *
 * Implements ISceneLoaderPluginAsync with:
 * - loadAsync as primary method (loads entire scene)
 * - importMeshAsync delegates to loadAsync
 *
 * Asset loading is handled by behaviors (Model, Image, Video),
 * not by the plugin. The plugin only creates TransformNodes with transforms
 * and extracts assets from ZIP archives for ResourceManager storage.
 */
export abstract class BaseTracePlugin implements ISceneLoaderPluginAsync {
    abstract readonly name: string;
    abstract readonly extensions: string | ISceneLoaderPluginExtensions;

    // =========================================================================
    // ISceneLoaderPluginAsync Implementation
    // =========================================================================

    /**
     * Primary method - loads entire scene.
     */
    async loadAsync(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (event: ISceneLoaderProgressEvent) => void,
        fileName?: string
    ): Promise<void> {
        const sceneStructure = await this.parseSceneData(data, rootUrl, fileName);
        await this.loadNodes(sceneStructure, scene, onProgress);
    }

    /**
     * Import meshes - delegates to loadAsync since we load entire scenes.
     *
     * Note: The meshesNames parameter is intentionally ignored. Scene composition
     * formats (.trace, .tracex, .glxf) load complete scene graphs with hierarchies
     * and behaviors. Mesh filtering would break parent-child relationships and
     * associated metadata. Use loadAsync() for full scene loading.
     */
    async importMeshAsync(
        meshesNames: string | readonly string[] | null | undefined,
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (event: ISceneLoaderProgressEvent) => void,
        fileName?: string
    ): Promise<ISceneLoaderAsyncResult> {
        // Warn if caller expects mesh filtering (which we don't support)
        if (meshesNames !== null && meshesNames !== undefined) {
            Logger.warn(
                `${this.name}: meshesNames parameter is ignored. ` +
                `Scene composition formats load complete scene graphs.`
            );
        }
        const beforeMeshes = new Set(scene.meshes);
        const beforeAnimations = new Set(scene.animationGroups);
        const beforeSkeletons = new Set(scene.skeletons);
        const beforeTransforms = new Set(scene.transformNodes);
        const beforeGeometries = new Set(scene.geometries);
        const beforeLights = new Set(scene.lights);

        await this.loadAsync(scene, data, rootUrl, onProgress, fileName);

        return {
            meshes: scene.meshes.filter(m => !beforeMeshes.has(m)),
            animationGroups: scene.animationGroups.filter(a => !beforeAnimations.has(a)),
            skeletons: scene.skeletons.filter(s => !beforeSkeletons.has(s)),
            transformNodes: scene.transformNodes.filter(t => !beforeTransforms.has(t)),
            geometries: scene.geometries.filter(g => !beforeGeometries.has(g)),
            lights: scene.lights.filter(l => !beforeLights.has(l)),
            particleSystems: [],
            spriteManagers: [],
        };
    }

    /**
     * Load into an asset container.
     */
    async loadAssetContainerAsync(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (event: ISceneLoaderProgressEvent) => void,
        fileName?: string
    ): Promise<AssetContainer> {
        const container = new AssetContainer(scene);
        const sceneStructure = await this.parseSceneData(data, rootUrl, fileName);
        await this.loadNodesIntoContainer(sceneStructure, container, scene, onProgress);
        return container;
    }

    // =========================================================================
    // Public API for TraceScene Integration
    // =========================================================================

    /**
     * Load a scene and return the LoadedSceneResult directly.
     * This is the preferred method for TraceScene integration.
     */
    async loadWithResult(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (loaded: number, total: number) => void,
        fileName?: string
    ): Promise<LoadedSceneResult> {
        const structure = await this.parseSceneData(data, rootUrl, fileName);
        const progressAdapter = onProgress
            ? (event: ISceneLoaderProgressEvent) => {
                onProgress(event.loaded, event.total);
            }
            : undefined;
        return await this.loadNodes(structure, scene, progressAdapter);
    }

    // =========================================================================
    // Abstract Methods
    // =========================================================================

    /**
     * Parse format-specific scene data into common structure.
     */
    protected abstract parseSceneData(
        data: unknown,
        rootUrl: string,
        fileName?: string
    ): Promise<ParsedSceneStructure>;

    // =========================================================================
    // Protected Methods - Shared Functionality
    // =========================================================================

    /**
     * Load all nodes into the scene and return LoadedSceneResult.
     * Creates TransformNodes with transforms - actual asset loading is done by behaviors.
     */
    protected async loadNodes(
        structure: ParsedSceneStructure,
        scene: Scene,
        onProgress?: (event: ISceneLoaderProgressEvent) => void
    ): Promise<LoadedSceneResult> {
        const loadedNodes: LoadedSceneNode[] = [];
        const nodeMap = new Map<string, LoadedSceneNode>();

        // Passes 1 & 2: Build transform hierarchy
        const { nodeTransforms, nodePaths } = buildTransformHierarchy(structure.nodes, scene);

        // Pass 3: Create LoadedSceneNode objects
        for (let i = 0; i < structure.nodes.length; i++) {
            const node = structure.nodes[i];
            const transform = nodeTransforms.get(node)!;
            const path = nodePaths.get(node)!;

            const loadedNode: LoadedSceneNode = {
                transform,
                name: node.name,
                src: node.src,
                active: node.active,
                behaviors: node.behaviors,
                resetAssetTransform: node.resetAssetTransform,
            };

            loadedNodes.push(loadedNode);
            nodeMap.set(path, loadedNode);

            // Report progress
            onProgress?.({
                lengthComputable: true,
                loaded: i + 1,
                total: structure.nodes.length,
            });
        }

        // Find root nodes (no parent)
        const rootNodes = loadedNodes.filter(n => !n.transform.parent);

        return {
            nodes: loadedNodes,
            rootNodes,
            nodeMap,
            assetBlobs: structure.assetBlobs,
            scripts: structure.scripts,
        };
    }

    /**
     * Load nodes into an asset container.
     * Creates TransformNodes with transforms - actual asset loading is done by behaviors.
     */
    protected async loadNodesIntoContainer(
        structure: ParsedSceneStructure,
        container: AssetContainer,
        scene: Scene,
        onProgress?: (event: ISceneLoaderProgressEvent) => void
    ): Promise<void> {
        // Passes 1 & 2: Build transform hierarchy, adding each transform to container
        buildTransformHierarchy(structure.nodes, scene, (_node, transform) => {
            container.transformNodes.push(transform);
        });

        // Report progress
        for (let i = 0; i < structure.nodes.length; i++) {
            onProgress?.({
                lengthComputable: true,
                loaded: i + 1,
                total: structure.nodes.length,
            });
        }
    }
}
